<template>
    <div style="width: 100%">
        <div style="position: absolute; z-index: 5; top: 58px;display: flex;width: 60%;left: 20%;">
            <v-btn text
                style="color: #006C90; font-size: 14px; padding: 0"
                @click="changeMonth('back')" 
                data-test="incident-heatmap-back-btn"
                :style="{visibility: withinBackRange ? 'visible': 'hidden'}"
            >
                <v-icon class="fal fa-chevron-left calendar-heat-map-arrow-icon pr-2"></v-icon>
                <span>{{ lastMonth }}</span>
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn text
                style="color: #006C90; font-size: 14px; padding: 0"  
                @click="changeMonth('forward')" 
                data-test="incident-heatmap-forward-btn"
                :style="{visibility: withinForwardRange ? 'visible': 'hidden'}"
            >
                <span class="pr-2">{{ nextMonth }}</span>
                <v-icon small class="fal fa-chevron-right calendar-heat-map-arrow-icon"></v-icon>
            </v-btn>
        </div>
        <div v-if="!showChart">
            <fe-spinner class="spinner"/>
        </div>
        <highcharts
            v-else
            ref='calendar-heat-map'
            class="calendar-heat-map"
            :options="chartOptions"
        />
    </div>
</template>

<script>
    import { mapState } from 'vuex'
    import Highcharts from 'highcharts'
    import hcHeatmap from 'highcharts/modules/heatmap'
    import HighchartsBrokenAxis from 'highcharts/modules/broken-axis' 
    hcHeatmap(Highcharts)
    HighchartsBrokenAxis(Highcharts) // removes certain data (in this case removes weekends)
    import { colorBlindGreyLinedBar } from '@/components/charts/chartFormats/patterns/seriesPatterns'

    export default {
        props: {
            params: { 
                default: null 
            }, // initial data
            endpoint: { 
                type: String,
                default: null 
            }, // needed to grab new data
            height: { 
                type: String,
                default: '300' 
            },
            colorBlindMode: {
                type: Boolean,
                default: false
            },
            endpointParams: {
                type: Object,
                default: () => {}
            },
        },
        data() {
            return {
                weekdays: ['M', 'Tu', 'W', 'Th', 'F'],
                chartOptions: {},
                showChart: false,
                selectedMonthYr: this.$dayjs(new Date()).format('MMM YYYY'),
                currMonthYr: this.$dayjs(new Date()).format('MMM YYYY'),
                cohorts: null,
            }
        },
        computed: {
            ...mapState('global', ['defaultSchool']),
            withinBackRange() {
                let diff = this.$dayjs(this.selectedMonthYr).diff(this.$dayjs(this.currMonthYr), 'month') 
                return diff === 0 || diff === -1  // if starting month OR went forward a month 
            },
            withinForwardRange() {
                let diff = this.$dayjs(this.selectedMonthYr).diff(this.$dayjs(this.currMonthYr), 'month')
                return diff === -1 || diff == -2  // only show if back one or two months
            },
            lastMonth() {
                return this.$dayjs(this.selectedMonthYr).subtract(1, 'month').format('MMM')
            },
            nextMonth() {
                return this.$dayjs(this.selectedMonthYr).add(1, 'month').format('MMM')
            }
        },
        watch: {
            colorBlindMode(v) {
                this.chartOptions.colorAxis.dataClasses[0].color = v ? colorBlindGreyLinedBar : '#ECEDF1'
            },
            params: {
                deep: true,
                handler() {
                    if(this.params) {
                        let data = this.params.data ? this.params.data : this.params
                        this.buildChart(data)
                    } 
                }
            }
        },  
        mounted() {
            if(this.endpoint) {
                // make endpoint call
                this.$axios.get(this.$models.getUrl(this.endpoint, 'read') + '&' + this.$objectToParams(this.endpointParams))
                    .then(response => {
                        if(response.data.data) {
                            this.cohorts = response.data.cohorts
                            this.buildChart(response.data.data)
                        }
                    })
            }
        },
        methods: {
            generateCalendar(data) {
                if(data[0].date.includes('07-02')) {
                    data.unshift({
                        date: this.$dayjs(data[0].date).subtract(1, 'day').format('YYYY-MM-DD'),
                        count: 0,
                        cohort: 0
                    })
                }
                // Calculate the starting weekday index 
                let firstWeekday = new Date(data[0].date).getDay(),
                    monthLength = data.length,
                    lastElement = data[monthLength - 1].date,
                    lastWeekday = new Date(lastElement).getDay(),
                    lengthOfWeek = 6,
                    emptyTilesFirst = firstWeekday,
                    chartData = []

                // Add the empty tiles before the first day of the month with null values to
                // take up space in the chart
                if(emptyTilesFirst !== 6) {
                for (let emptyDay = 0; emptyDay < emptyTilesFirst; emptyDay++) {
                    chartData.push({
                        x: emptyDay,
                        y: 5,
                        value: null,
                        date: null,
                        custom: {
                            empty: true
                        }
                    })
                }
            }

                // Loop through and populate with count and dates from the dataset
                for (let day = 1; day <= data.length; day++) {
                    // Get date from the given data array
                    const date = data[day - 1].date
                    // Offset by the number of empty tiles
                    const xCoordinate = (emptyTilesFirst + day - 1) % 7
                    let skipWeekends = firstWeekday === 5 || firstWeekday === 6 ? 1 : 0
                    const yCoordinate = Math.floor((firstWeekday + day - 1) / 7) - skipWeekends // if weekend first day, adds blank first row
                    const id = day

                    // Get the corresponding count for the current day from the given
                    // array
                    let count = data[day - 1].count

                    let max = this.cohorts && this.cohorts[0]?.max || 4
                    let dataLabels = count < max && this.colorBlindMode ? {
                            enabled: true,
                            borderRadius: 0,
                            backgroundColor: '#ECEDF1',
                            padding: 4,
                            y: 0
                        }: {}

                    chartData.push({
                        x: xCoordinate,
                        y: 5 - yCoordinate,
                        value: count,
                        date: new Date(date).getTime(),
                        custom: {
                            monthDay: id
                        },
                        dataLabels: dataLabels
                    })
                }

                // Fill in the missing values when dataset is looped through.
                const emptyTilesLast = lengthOfWeek - lastWeekday
                for (let emptyDay = 1; emptyDay <= emptyTilesLast; emptyDay++) {
                    chartData.push({
                        x: (lastWeekday + emptyDay) % 7,
                        y: 0,
                        value: null,
                        date: null,
                        custom: {
                            empty: true
                        }
                    })
                }
                return chartData
            },
            buildChart(data) {
                let me = this
                this.chartOptions = {
                    chart: {
                        type: 'heatmap', 
                        height: me.height,
                        marginLeft: 10,
                        marginRight: 10,
                        marginTop: 70,
                        marginBottom: 10
                    },
                    title: {
                        text: me.selectedMonthYr,
                        style: {
                            fontWeight: '500',
                            color: '#050F2D',
                            fontSize: '16px'
                        }
                    },
                    tooltip: {
                        useHTML: true,
                        headerFormat: '',
                        pointFormatter() {
                            let month = me.$dayjs(me.selectedMonthYr).format('M')
                            let day = this.custom.monthDay 
                            let yr = me.$dayjs(me.selectedMonthYr).format('YYYY')
                            return `<div class='mb-2'><b>${month}/${day}/${yr}</b></div><div>Total Incidents: <b>${this.value}</b><div>`
                        },
                        nullFormatter() {
                            return 'No data'
                        }
                    },
                    xAxis: {
                        categories: me.weekdays,
                        opposite: true,
                        accessibility: {
                            description: 'weekdays'
                        },
                        labels: {
                            style: {
                                fontSize: '16px'
                            }
                        },
                        min: 0,
                        max: 4,
                        gridLineWidth: 0,
                        lineWidth: 0,
                        minorGridLineWidth: 0,
                        lineColor: 'transparent',
                        minorTickLength: 0,
                        tickLength: 0,
                        offset: -10,
                        breaks: me.generateWeekendBreaks(new Date(data[0].date), new Date(data[data.length - 1].date)),
                        ordinal: false,
                        type: 'datetime'
                    },
                    yAxis: {
                        min: 0,
                        max: 5,
                        accessibility: {
                            description: 'weeks'
                        },
                        visible: false,
                        endOnTick: false,
                        startOnTick: false,
                        gridLineWidth: 0
                    },
                    legend: {
                        labelFormatter() {
                            return this.name.replace(/.00/g, '').replace('>','&#8805;').replace('<', '&#8804;')
                        },
                        align: 'center',
                        symbolRadius: 2,
                        margin: 0,
                        paddingBottom: 0
                    },
                    colorAxis: {
                        dataClasses: []
                    },
                    series: [{
                        keys: ['x', 'y', 'value', 'date', 'id'],
                        data: me.generateCalendar(data),
                        nullColor: '#fff',
                        borderWidth: 2,
                        borderColor: '#fff',
                        dataLabels: [{
                            enabled: true,
                            style: {
                                textOutline: 'none',
                                fontWeight: 'normal',
                                fontSize: '1rem',
                            },
                            y: 1,
                            formatter: function() {
                                if (this.point.custom.empty) return
                                if (this.color === '#2B87FF' || this.color.pattern) {
                                    return '<span style="color: #000;">' + this.point.value + '</span>'
                                }
                                return this.point.value
                            }
                        }]
                    }],
                    plotOptions: {
                        series: {
                            cursor: 'pointer',
                            stacking: 'normal',
                            point: {
                                events: {
                                    click(e) {
                                        me.$emit('heatmapClicked', me.selectedMonthYr, e)
                                    }
                                }
                            }
                        }
                    },
                }
                if(me.cohorts) {
                    if(me.cohorts[1].min !== 0) { // if min of middle cohort is 0, means all values are 0
                        this.chartOptions.colorAxis.dataClasses = [
                            {
                                color: me.colorBlindMode ? colorBlindGreyLinedBar : '#ECEDF1',
                                to: me.cohorts[0]?.max || 3
                            }, {
                                color: '#2B87FF',
                                from: me.cohorts[1].min || 4,
                                to: me.cohorts[1].max || 7
                            }, {
                                color: '#0028A1',
                                from: me.cohorts[2].min || 8
                            }
                        ]
                    } else {
                        this.chartOptions.colorAxis.dataClasses = [
                            {
                                color: me.colorBlindMode ? colorBlindGreyLinedBar : '#ECEDF1',
                                to: 3
                            }, {
                                color: '#2B87FF',
                                from: 4,
                                to: 7
                            }, {
                                color: '#0028A1',
                                from: 8
                            }
                        ]
                    }
                }
                this.showChart = true
            },
            changeMonth(direction) {
                if(direction == 'back') {
                    this.selectedMonthYr = this.$dayjs(this.selectedMonthYr).subtract(1, 'month').format('MMM YYYY')
                } else {
                    this.selectedMonthYr = this.$dayjs(this.selectedMonthYr).add(1, 'month').format('MMM YYYY')
                }
                this.$refs['calendar-heat-map']?.chart.title.update({text: this.selectedMonthYr})

                let monthYr = this.$dayjs(this.selectedMonthYr).format('YYYY-MM')
                let month = this.$dayjs(this.selectedMonthYr).format('MM')
                let start = month === '07' ? monthYr + '-02' : monthYr + '-01'
                let end =  monthYr + '-' + this.$dayjs(start).endOf('month').$D
                let school_id = this.defaultSchool?.id
                let p = { start: start, end: end, school_id: school_id }

                if(this.endpoint) {
                    this.showChart = false
                    this.$axios.get(this.$models.getUrl(this.endpoint, 'read') + '&' + this.$objectToParams(p))
                        .then(response => {
                            if(response.data.data) {
                                this.cohorts = response.data.cohorts
                                this.buildChart(response.data.data)
                            }
                        })
                }
            },
            generateWeekendBreaks(startDate, endDate) {
                const breaks = []
                let currentDate = new Date(startDate)

                while (currentDate <= endDate) {
                    const dayOfWeek = currentDate.getDay()
                    if (dayOfWeek === 6) { // Saturday
                        breaks.push({
                            from: currentDate.getTime(),
                            to: new Date(currentDate.getTime() + 2 * 24 * 3600 * 1000).getTime() // Add 2 days to include Sunday
                        });
                    }
                    currentDate.setDate(currentDate.getDate() + 1)
                }
                return breaks
            }
        }
    }
</script>

<style lang="scss" scoped>
.calendar-heat-map {
    ::v-deep * {
        font-family: 'HyperlegibleSans-Regular', Hyperlegible Sans, 'Roboto', sans-serif !important;
    }
} 

.spinner {
    position: absolute;
    left: 0;
    right: 0;
    margin: auto;
    line-height: 250px !important;
}

.calendar-heat-map-arrow-icon {
    font-size: 12px !important;
}
</style>
